import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import axios from 'axios';
import { useUser } from '@/contexts/UserContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useHeader } from '@/contexts/HeaderContext';
import { useTheme } from '@/contexts/ThemeContext';
import Loading from '../components/Loading';
import Modal from '../components/Modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/* ── helpers ─────────────────────────────────────── */
const getDateLabel = (dateString, lang) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(lang?.startsWith('it') ? 'it-IT' : 'en-US', {
    day: 'numeric', month: 'short',
  }).toUpperCase();
};

const statusIcon = (status) => {
  if (status === 'open') return 'solar:clock-circle-linear';
  if (status === 'in_progress') return 'solar:settings-linear';
  return 'solar:check-circle-linear';
};

const statusColor = (status) => {
  if (status === 'open') return '#7EA86C';
  if (status === 'in_progress') return '#D4A843';
  return 'var(--c-texTer)';
};

const mono = 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace';

/* ════════════════════════════════════════════════════
   Support — journal-style redesign
   ════════════════════════════════════════════════════ */
const Support = () => {
  const { t, i18n } = useTranslation();
  const { user: contextUser, loading: userLoading } = useUser();
  const { setHeaderProps } = useHeader();
  const { isLightMode } = useTheme();
  const user = contextUser;

  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [form, setForm] = useState({
    type: 'support',
    subject: '',
    description: '',
    email: user?.email || '',
    user_id: user?.user_id || null,
    images: [],
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  /* ── Auth helper ───────────────────────────────── */
  const getAuthToken = async () => {
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (err) {
      console.error('Error getting auth token:', err);
      return null;
    }
  };

  /* ── API ────────────────────────────────────────── */
  const fetchTickets = useCallback(async () => {
    try {
      setLoadingTickets(true);
      const token = await getAuthToken();
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : { withCredentials: true };
      const res = await axios.get(`${API}/support/my/open`, cfg);
      setTickets(res.data || []);
    } catch (err) {
      console.error('[Support] fetch error', err);
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  }, []);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingImages(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload/support-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        urls.push(res.data.url);
      }
      setForm((p) => ({ ...p, images: [...p.images, ...urls] }));
    } catch (err) {
      console.error('Upload error', err);
      toast.error(t('support.form.uploadError', 'Upload failed'));
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (idx) => setForm((p) => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.user_id) { toast.error(t('support.form.notAuthenticated', 'You must be logged in.')); return; }
    try {
      setLoading(true);
      const token = await getAuthToken();
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : { withCredentials: true };
      await axios.post(`${API}/support`, { ...form, type: selectedType }, cfg);
      setShowFormModal(false);
      setSelectedType(null);
      resetForm();
      await fetchTickets();
      toast.success(t('support.form.success', 'Request sent!'));
    } catch (err) {
      console.error('[Support] submit error', err);
      toast.error(t('support.form.error', 'Error creating ticket'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm({ type: 'support', subject: '', description: '', email: user?.email || '', user_id: user?.user_id || null, images: [] });

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setForm((p) => ({ ...p, type }));
    setShowTypeModal(false);
    setShowFormModal(true);
  };

  /* ── Effects ────────────────────────────────────── */
  useEffect(() => {
    setForm((p) => ({ ...p, email: user?.email || '', user_id: user?.user_id || null }));
  }, [user?.email, user?.user_id]);

  useEffect(() => {
    if (user) fetchTickets();
  }, [user, fetchTickets]);

  useEffect(() => {
    setHeaderProps({
      title: t('support.title', 'Support'),
      user,
      rightElement: (
        <button
          onClick={() => setShowTypeModal(true)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: isLightMode ? '#191919' : '#ffffff',
            padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          aria-label="New request"
        >
          <Icon icon="solar:add-circle-linear" width={26} style={{ color: isLightMode ? '#191919' : '#ffffff' }} />
        </button>
      ),
    });
  }, [t, user, setHeaderProps, isLightMode]);

  /* ── Single ticket row (card style) ─────────────── */
  const TicketEntry = ({ ticket }) => (
    <motion.button
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      onClick={() => setSelectedTicket(ticket)}
      style={{
        width: '100%', textAlign: 'left',
        background: 'var(--c-surface-card)',
        borderRadius: 16, padding: '16px 18px',
        marginBottom: 12, border: '1px solid var(--c-border)',
        cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      {/* date + status pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--c-texTer)', fontFamily: mono }}>
          {getDateLabel(ticket.created_at, i18n.language)}
        </span>
        <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--c-texDis)', flexShrink: 0 }} />
        <span style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: statusColor(ticket.status), fontFamily: mono, fontWeight: 600 }}>
          {ticket.status === 'open' ? t('support.status.open', 'Open')
            : ticket.status === 'in_progress' ? t('support.status.inProgress', 'In progress')
            : t('support.status.closed', 'Closed')}
        </span>
      </div>

      {/* subject */}
      <span style={{ fontSize: 15, color: 'var(--c-texPri)', fontFamily: 'Manrope-Variable', fontWeight: 600, lineHeight: 1.4 }}>
        {ticket.subject}
      </span>

      {/* description excerpt */}
      {ticket.description && (
        <span style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--c-texSec)', fontFamily: 'Manrope-Variable', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          "{ticket.description}"
        </span>
      )}
    </motion.button>
  );

  /* ── Ticket detail view ─────────────────────────── */
  const TicketDetail = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ padding: '8px 24px 40px' }}>
      {/* toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <button
          onClick={() => setSelectedTicket(null)}
          style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--c-texSec)', fontSize: 14, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Manrope-Variable' }}
        >
          <Icon icon="solar:alt-arrow-left-linear" width={18} />
          {i18n.language?.startsWith('it') ? 'Indietro' : i18n.language?.startsWith('es') ? 'Atrás' : 'Back'}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: 'var(--c-surface-card)', border: '1px solid var(--c-border)' }}>
          <Icon icon={statusIcon(selectedTicket.status)} width={13} style={{ color: statusColor(selectedTicket.status) }} />
          <span style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: statusColor(selectedTicket.status), fontFamily: mono, fontWeight: 600 }}>
            {selectedTicket.status === 'open' ? t('support.status.open', 'Open')
              : selectedTicket.status === 'in_progress' ? t('support.status.inProgress', 'In progress')
              : t('support.status.closed', 'Closed')}
          </span>
        </div>
      </div>

      {/* date + type */}
      <span style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--c-texTer)', display: 'block', marginBottom: 12, fontFamily: mono }}>
        {getDateLabel(selectedTicket.created_at, i18n.language)}
        {selectedTicket.type && (
          <> · {selectedTicket.type === 'support' ? t('support.type.support', 'Support') : t('support.type.feature', 'Feature')}</>
        )}
      </span>

      {/* title */}
      <h1 style={{ fontSize: 24, fontWeight: 500, color: 'var(--c-texPri)', marginBottom: 16, fontFamily: 'Manrope-Variable', lineHeight: 1.3 }}>
        {selectedTicket.subject}
      </h1>

      {/* description */}
      <p style={{ color: 'var(--c-texPri)', whiteSpace: 'pre-wrap', lineHeight: 1.7, fontStyle: 'italic', marginBottom: 24, fontFamily: 'Manrope-Variable', fontSize: 15 }}>
        {selectedTicket.description}
      </p>

      {/* images */}
      {selectedTicket.images?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 24 }}>
          {selectedTicket.images.map((url, i) => (
            <img key={i} src={url} alt="" style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--c-border)' }} />
          ))}
        </div>
      )}

      {/* metadata */}
      <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 16 }}>
        {selectedTicket.email && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon icon="solar:letter-linear" width={13} style={{ color: 'var(--c-texTer)' }} />
            <span style={{ fontSize: 12, color: 'var(--c-texTer)', fontFamily: 'Manrope-Variable' }}>{selectedTicket.email}</span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon icon="solar:hashtag-linear" width={13} style={{ color: 'var(--c-texTer)' }} />
          <span style={{ fontSize: 12, color: 'var(--c-texTer)', fontFamily: mono }}>{selectedTicket.ticket_id}</span>
        </div>
      </div>
    </motion.div>
  );

  /* ── Render ─────────────────────────────────────── */
  if (!user) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'inherit' }}>
        <p style={{ fontSize: 15, color: 'var(--c-texTer)', fontStyle: 'italic', fontFamily: 'Manrope-Variable' }}>
          {t('support.notAuthenticated', 'Log in to view your requests.')}
        </p>
      </div>
    );
  }

  if (loadingTickets && tickets.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center page-container" style={{ background: 'inherit' }}>
        <Loading size="hundred" message={null} />
      </div>
    );
  }

  return (
    <div style={{ background: 'inherit' }}>
      <AnimatePresence mode="wait">
        {/* ── Detail view ──────────────────────────── */}
        {selectedTicket && <TicketDetail key="detail" />}

        {/* ── Type selection modal ─────────────────── */}
        {showTypeModal && (
          <Modal key="type" placement="bottom" onClose={() => setShowTypeModal(false)} ariaLabel={i18n.language?.startsWith('it') ? 'Tipo richiesta' : i18n.language?.startsWith('es') ? 'Tipo de solicitud' : 'Request type'}>
            <div>
              <div style={{ width: 46, height: 4, background: 'var(--c-border)', borderRadius: 999, margin: '6px auto 24px' }} />
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--c-texSec)', textTransform: 'uppercase', letterSpacing: '0.26em', marginBottom: 32 }}>
                {t('support.selectType', i18n.language?.startsWith('it') ? 'Tipo di richiesta' : i18n.language?.startsWith('es') ? 'Tipo de solicitud' : 'Request type')}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Support option */}
                <button
                  onClick={() => handleTypeSelect('support')}
                  style={{
                    width: '100%', textAlign: 'left', padding: '16px 14px',
                    background: 'var(--c-surface-card)', borderRadius: 14,
                    border: '1px solid var(--c-border)', cursor: 'pointer',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                  }}
                >
                  <Icon icon="solar:chat-round-dots-linear" width={20} style={{ color: '#7EA86C', marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <span style={{ display: 'block', fontSize: 15, color: 'var(--c-texPri)', fontFamily: 'Manrope-Variable', fontWeight: 500, lineHeight: 1.4 }}>
                      {t('support.type.supportTitle', 'Technical Support')}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--c-texSec)', fontStyle: 'italic', fontFamily: 'Manrope-Variable' }}>
                      {t('support.type.supportDesc', 'Need help or found a bug')}
                    </span>
                  </div>
                </button>

                {/* Feature option */}
                <button
                  onClick={() => handleTypeSelect('feature')}
                  style={{
                    width: '100%', textAlign: 'left', padding: '16px 14px',
                    background: 'var(--c-surface-card)', borderRadius: 14,
                    border: '1px solid var(--c-border)', cursor: 'pointer',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                  }}
                >
                  <Icon icon="solar:lightbulb-linear" width={20} style={{ color: '#D4A843', marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <span style={{ display: 'block', fontSize: 15, color: 'var(--c-texPri)', fontFamily: 'Manrope-Variable', fontWeight: 500, lineHeight: 1.4 }}>
                      {t('support.type.featureTitle', 'Suggest a Feature')}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--c-texSec)', fontStyle: 'italic', fontFamily: 'Manrope-Variable' }}>
                      {t('support.type.featureDesc', 'An idea to improve the app')}
                    </span>
                  </div>
                </button>
              </div>

              <button
                onClick={() => setShowTypeModal(false)}
                style={{
                  width: '100%', marginTop: 20, padding: '10px 0',
                  color: 'var(--c-texTer)', background: 'none', border: 'none',
                  cursor: 'pointer', textTransform: 'uppercase', fontSize: 13, letterSpacing: '0.08em',
                }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </Modal>
        )}

        {/* ── Form modal ──────────────────────────── */}
        {showFormModal && (
          <Modal key="form" placement="bottom" onClose={() => { setShowFormModal(false); setSelectedType(null); resetForm(); }} ariaLabel={selectedType === 'support' ? 'Support request' : 'Feature request'}>
            <div>
              <div style={{ width: 46, height: 4, background: 'var(--c-border)', borderRadius: 999, margin: '6px auto 24px' }} />
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--c-texSec)', textTransform: 'uppercase', letterSpacing: '0.26em', marginBottom: 28 }}>
                {selectedType === 'support' ? t('support.form.supportTitle', 'Support Request') : t('support.form.featureTitle', 'Suggest Feature')}
              </div>

              <form onSubmit={handleSubmit} style={{ fontFamily: 'Manrope-Variable', display: 'flex', flexDirection: 'column', gap: 18 }}>
                {/* Subject */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--c-texTer)', marginBottom: 6 }}>{t('support.form.subject', 'Subject')}</label>
                  <input
                    type="text" name="subject" value={form.subject}
                    onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))} required
                    style={{
                      width: '100%', height: 42, borderRadius: 21, padding: '0 16px',
                      border: '1px solid var(--c-border)', background: 'transparent',
                      color: 'var(--c-texPri)', outline: 'none', fontSize: 16,
                      fontFamily: 'Manrope-Variable',
                    }}
                    placeholder={selectedType === 'support' ? t('support.form.subjectSupportPlaceholder', 'Describe the issue...') : t('support.form.subjectFeaturePlaceholder', 'Describe the feature...')}
                  />
                </div>

                {/* Description */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--c-texTer)', marginBottom: 6 }}>{t('support.form.description', 'Description')}</label>
                  <textarea
                    name="description" value={form.description}
                    onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} required
                    style={{
                      width: '100%', minHeight: 120, borderRadius: 16, padding: '12px 16px',
                      border: '1px solid var(--c-border)', background: 'transparent',
                      color: 'var(--c-texPri)', outline: 'none', resize: 'none',
                      lineHeight: 1.6, fontSize: 16, fontFamily: 'Manrope-Variable',
                    }}
                    placeholder={selectedType === 'support' ? t('support.form.descriptionSupportPlaceholder', 'Explain the issue...') : t('support.form.descriptionFeaturePlaceholder', 'Explain your idea...')}
                  />
                </div>

                {/* Email */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--c-texTer)', marginBottom: 6 }}>{t('support.form.email', 'Reply email')}</label>
                  <input
                    type="email" name="email" value={form.email}
                    onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} required
                    style={{
                      width: '100%', height: 42, borderRadius: 21, padding: '0 16px',
                      border: '1px solid var(--c-border)', background: 'transparent',
                      color: 'var(--c-texPri)', outline: 'none', fontSize: 16,
                      fontFamily: 'Manrope-Variable',
                    }}
                    placeholder={t('support.form.emailPlaceholder', 'your@email.com')}
                  />
                </div>

                {/* Images */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--c-texTer)', marginBottom: 6 }}>{t('support.form.images', 'Images (optional)')}</label>
                  <div
                    onClick={() => !uploadingImages && document.getElementById('support-image-input').click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); if (!uploadingImages) handleImageUpload({ target: { files: Array.from(e.dataTransfer.files) } }); }}
                    style={{
                      border: '1px dashed var(--c-border)', borderRadius: 16,
                      padding: '20px 16px', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      opacity: uploadingImages ? 0.5 : 1, transition: 'opacity 0.2s',
                    }}
                  >
                    <input id="support-image-input" type="file" multiple accept="image/*" onChange={handleImageUpload} disabled={uploadingImages} style={{ display: 'none' }} />
                    <Icon icon="solar:gallery-add-linear" width={22} style={{ color: 'var(--c-texTer)', marginBottom: 4 }} />
                    <span style={{ fontSize: 12, color: 'var(--c-texTer)' }}>
                      {uploadingImages ? t('support.form.uploading', 'Uploading...') : t('support.form.dragImages', 'Tap to add images')}
                    </span>
                  </div>
                  {form.images.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
                      {form.images.map((url, i) => (
                        <div key={i} style={{ position: 'relative' }}>
                          <img src={url} alt="" style={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--c-border)' }} />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                            style={{
                              position: 'absolute', top: -4, right: -4,
                              width: 18, height: 18, borderRadius: '50%',
                              background: '#e53e3e', color: '#fff', fontSize: 10,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: 'none', cursor: 'pointer',
                            }}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 16, borderTop: '1px solid var(--c-border)' }}>
                  <button
                    type="button"
                    onClick={() => { setShowFormModal(false); setSelectedType(null); resetForm(); }}
                    style={{
                      padding: '8px 16px', color: 'var(--c-texTer)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      textTransform: 'uppercase', fontSize: 13, letterSpacing: '0.06em',
                    }}
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    type="submit" disabled={loading || !user?.user_id}
                    style={{
                      padding: '10px 24px', borderRadius: 10,
                      background: '#5C8748', color: '#fff',
                      fontWeight: 600, textTransform: 'uppercase', fontSize: 13,
                      letterSpacing: '0.06em', border: 'none', cursor: 'pointer',
                      opacity: (loading || !user?.user_id) ? 0.5 : 1,
                    }}
                  >
                    {loading ? t('support.form.sending', 'Sending...') : t('support.form.send', 'Send')}
                  </button>
                </div>
              </form>
            </div>
          </Modal>
        )}

        {/* ── Ticket list ──────────────────────────── */}
        {!selectedTicket && !showTypeModal && !showFormModal && (
          <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ padding: '24px 20px 140px' }}>
            {tickets.length > 0 ? (
              tickets.map((ticket) => <TicketEntry key={ticket.ticket_id} ticket={ticket} />)
            ) : (
              <div style={{ textAlign: 'center', paddingTop: 48 }}>
                <Icon icon="solar:chat-round-dots-linear" width={36} style={{ color: 'var(--c-texDis)', marginBottom: 12 }} />
                <p style={{ fontSize: 15, color: 'var(--c-texTer)', fontStyle: 'italic', fontFamily: 'Manrope-Variable' }}>
                  {t('support.empty', 'No requests yet.')}
                </p>
                <p style={{ fontSize: 13, color: 'var(--c-texDis)', fontFamily: 'Manrope-Variable', marginTop: 8 }}>
                  {i18n.language?.startsWith('it') ? 'Tocca + per creare la tua prima richiesta' : i18n.language?.startsWith('es') ? 'Toca + para crear tu primera solicitud' : 'Tap + to create your first request'}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Support;
